import React from "react";

// Style
import "./DrugImpurity.scss";

// Classnames
import clsx from "clsx";

// Components
import Img from "components/Img";

// Lib
import { Link } from "react-router-dom";
import { decodeHtmlEntity, getImg } from "lib/helpers";

// Type
export type TItemDrugs = {
  drugName?: string,
  drugEbcId?: string,
  drugSmiles?: string,
  drugCanBuy?: boolean,
}
export type TItemImpurity = {
  drugName?: string,
  impurityName?: string,
  impurityEbcId?: string,
  impuritySmiles?: string,
  impurityCanBuy?: boolean,
}
type TItem = TItemDrugs | TItemImpurity;
type TArgs = {
  type?: string,
  data?: TItem[],
  title?: string,
  drugSmiles?: string,
}

// Interface
interface IDrugImpurity {
  isCart?: boolean,
  className?: string,
  drugForImp?: TItemDrugs[];
  impForDrug?: TItemImpurity[];
}

export const DrugImpurity: React.FC<IDrugImpurity> = (props) => {
  const { impForDrug, drugForImp, className, isCart } = props;

  const sortData = (data: TItem[]) => {
    return [...data].sort((a, b) => {
      const aCanBuy = "drugCanBuy" in a ? a.drugCanBuy : "impurityCanBuy" in a ? a?.impurityCanBuy : "";
      const bCanBuy = "drugCanBuy" in b ? b.drugCanBuy : "impurityCanBuy" in b ? b?.impurityCanBuy : "";

      return (bCanBuy ? 1 : 0) - (aCanBuy ? 1 : 0);
    });
  };

  const getContent = ({ type, data, title }: TArgs) => {
    const sortedData = sortData(data || []);
    return (<>
      <p className="drug-impurity__text-span">
        {title}
      </p>
      {sortedData?.map((item: any, index: number) => {
          let value;

          if (item.drugEbcId) {
            if (item.drugName) {
              value = `${item.drugName} (${item.drugEbcId})`;
            } else {
              value = item.drugEbcId;
            }
          } else if (item.impurityName) {
            value = `${item.impurityName} (${item.impurityEbcId})`;
          } else {
            value = item.impurityEbcId;
          }

          const url = `/molecule-product/${item?.drugEbcId || item?.impurityEbcId}`;
          const src = getImg(
            item?.drugSmiles || item?.impuritySmiles,
            { w: 500, h: 500 }
          );

          const cnWrapperImgTag = clsx(
            "drug-impurity__wrapper-img-tag",
            isCart ? "drug-impurity__wrapper-img-tag--active flex flex-ai-start" : ""
          );
          const cnImage = clsx(
            "drug-impurity__img-tag",
            isCart ? "drug-impurity__img-tag--active" : ""
          );

          return (
            <div
              key={item?.drugSmiles || item.impuritySmiles}
              className="drug-impurity__wrapper-text-content mr10 mb5 relative"
            >
              {item.drugCanBuy || item.impurityCanBuy ? (
                <>
                  <div className="drug-impurity__wrapper-img-hover">
                    <div className={cnWrapperImgTag}>
                      <Img
                        isPreloader
                        innerSrc={src}
                        className={cnImage}
                        alt="Molecule product"
                      />
                      {isCart && (
                        <div className="drug-impurity__wrapper-content">
                          <div className="flex flex-ai-center mb5">
                            <p className="drug-impurity__label-cart mr10">ID</p>
                            <p className="drug-impurity__text-cart drug-impurity__cart-link">
                              <Link
                                to={url}
                                target="_blank"
                                className="drug-impurity__text-p drug-impurity__cart-link"
                              >
                                {item.drugEbcId || item.impurityEbcId}
                              </Link>
                            </p>
                          </div>
                          <div className="flex flex-ai-center">
                            <p className="drug-impurity__label-cart mr10">Name</p>
                            <p
                              className="drug-impurity__text-cart"
                              dangerouslySetInnerHTML={{ __html: item.drugName || item?.impurityName }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {isCart ?
                    <div className="drug-impurity__text-p drug-impurity__number">
                      {type === 'drugs' && <p
                        dangerouslySetInnerHTML={{
                          __html:
                            item?.drugName?.length > 40 || !item?.drugName
                              ? `# ${index + 1}`
                              : item.drugName
                        }}
                      />}
                      {type === 'impurities' && <p
                        dangerouslySetInnerHTML={{
                          __html:
                            item?.impurityName?.length > 40 || !item?.impurityName
                              ? `# ${index + 1}`
                              : item.impurityName
                        }}
                      />}
                    </div>
                    : <Link
                      to={url}
                      target="_blank"
                      className="drug-impurity__text-p"
                    >
                      <p
                        dangerouslySetInnerHTML={{ __html: decodeHtmlEntity(value) }}
                        className="drug-impurity__text-p flex flex-ai-center flex-wrap"
                      />
                    </Link>}
                </>
              ) : (
                <p className="drug-impurity__text-no-can-buy">
                  {item.drugName || item.impurityName}
                </p>
              )}
            </div>
          );
        }
      )}
    </>);
  };

  const cnWrapper = clsx(
    "drug-impurity flex flex-wrap flex-ai-start flex-direction-column-desktop",
    className ? className : "",
    isCart ? "drug-impurity--cart" : "",
    impForDrug?.length ? "drug-impurity__text-impurities" : "",
    drugForImp?.length ? "drug-impurity__text-drug-impurities" : "",
    !impForDrug?.length || drugForImp?.length ? "drug-impurity--active" : ""
  );

  const type = impForDrug?.length ? 'impurities' : 'drugs';

  const data = impForDrug?.length
    ? impForDrug
    : drugForImp;

  return data?.length ? (
    <div className={cnWrapper}>
      {getContent({
        type: type,
        data: data,
        title: impForDrug?.length ? `${isCart ? "Impurities" : "Impurity"}` : "The impurity for drugs"
      })}
    </div>
  ) : <></>;
};

export default DrugImpurity;